import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Polls unread messages every 20s and shows a toast whenever the total
 * unread count increases (i.e. a new message arrived). Suppressed on
 * the /nachrichten page since the chat handles its own updates there.
 */
export function MessageNotifier() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const lastUnread = useRef(null); // null = not initialised yet
  const lastPartnerAts = useRef({}); // partnerId -> last_at ISO

  useEffect(() => {
    if (!user) {
      lastUnread.current = null;
      lastPartnerAts.current = {};
      return;
    }
    let alive = true;

    const check = async () => {
      try {
        const partners = await api.get("/messages/users").then((r) => r.data).catch(() => []);
        if (!alive) return;
        const total = partners.reduce((s, p) => s + (p.unread || 0), 0);
        const onChatPage = location.pathname.startsWith("/nachrichten");
        // On first run just seed and skip toast
        if (lastUnread.current === null) {
          lastUnread.current = total;
          for (const p of partners) lastPartnerAts.current[p.id] = p.last_at || "";
          return;
        }
        // Determine partners with strictly-newer last_at AND unread > 0
        const newlyArrived = partners.filter((p) => {
          const prev = lastPartnerAts.current[p.id] || "";
          const isNew = (p.last_at || "") > prev;
          return isNew && (p.unread || 0) > 0;
        });
        // Refresh cache
        for (const p of partners) lastPartnerAts.current[p.id] = p.last_at || "";

        if (!onChatPage && newlyArrived.length > 0) {
          for (const p of newlyArrived.slice(0, 3)) {
            toast(
              (t) => (
                <button
                  type="button"
                  className="flex items-start gap-3 text-left w-full"
                  onClick={() => {
                    toast.dismiss(t);
                    navigate("/nachrichten");
                  }}
                  data-testid={`toast-msg-${p.id}`}
                >
                  <MessageSquare className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">
                      Neue Nachricht von {p.name || p.email}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {p.last_message || "—"}
                    </div>
                  </div>
                </button>
              ),
              { duration: 8000 }
            );
          }
        }
        lastUnread.current = total;
      } catch { /* ignore transient errors */ }
    };

    check();
    const iv = setInterval(check, 20000);
    return () => { alive = false; clearInterval(iv); };
  }, [user, location.pathname, navigate]);

  return null;
}
